import { smbManifestSchema, type SmbManifest } from "./smb";

export const selectionKeys = ["staticAdUnitTypeIds", "ugcStyleValues", "slideshowStyleValues", "memeTemplateIds"] as const;
export type SmbPackageSelection = SmbManifest["personaSections"][number];

export function selectionCount(section: SmbPackageSelection) {
  return selectionKeys.reduce((count, key) => count + section[key].length, 0);
}

/** Merge into the latest draft only; never consolidate legacy sections implicitly. */
export function attachUploadedStaticSelections(manifest: SmbManifest, ids: string[]): SmbManifest {
  if (!hasSharedSelections(manifest)) return manifest;
  const current = manifest.personaSections[0];
  const additions = [...new Set(ids)].filter(id => !current.staticAdUnitTypeIds.includes(id))
    .slice(0, Math.max(0, 10 - selectionCount(current)));
  return withSharedSelections(manifest, {
    ...current, staticAdUnitTypeIds: [...current.staticAdUnitTypeIds, ...additions],
  });
}

/** Order within a picker does not make otherwise identical selections legacy. */
export function hasSharedSelections(manifest: SmbManifest) {
  const first = manifest.personaSections[0];
  return manifest.personaSections.every(section => selectionKeys.every(key =>
    section[key].length === first[key].length && section[key].every(id => first[key].includes(id))));
}

/** A review proposal only: deliberately unvalidated so >10 choices can be resolved. */
export function proposeSharedSelections(manifest: SmbManifest): SmbPackageSelection {
  return Object.fromEntries(selectionKeys.map(key =>
    [key, [...new Set(manifest.personaSections.flatMap(section => section[key]))]])) as SmbPackageSelection;
}

/** Only explicit shared editing/consolidation should call this projection. */
export function withSharedSelections(manifest: SmbManifest, selection: SmbPackageSelection): SmbManifest {
  return smbManifestSchema.parse({ ...manifest,
    personaSections: Array.from({ length: 3 }, () =>
      Object.fromEntries(selectionKeys.map(key => [key, [...selection[key]]]))),
  });
}
